/**
 * Approval status pill component
 * 
 * Displays the approval state of a decision trace:
 * - Not Requested
 * - Pending
 * - Approved
 * - Rejected
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Clock, XCircle, Circle } from 'lucide-react';

const STATES = {
  none: {
    icon: Circle,
    label: 'Not Requested',
    className: 'bg-slate-500/10 text-slate-300 border-slate-500/30'
  },
  pending: {
    icon: Clock,
    label: 'Pending Approval',
    className: 'bg-amber-500/10 text-amber-300 border-amber-500/30'
  },
  approved: {
    icon: CheckCircle,
    label: 'Approved',
    className: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
  },
  rejected: {
    icon: XCircle,
    label: 'Rejected',
    className: 'bg-rose-500/10 text-rose-300 border-rose-500/30'
  }
};

/**
 * Determine the approval state from a trace object
 */
export function getApprovalState(trace) {
  if (!trace) return 'none';
  
  const hasRejection = trace.metadata?.rejected_at || trace.metadata?.rejected_by;
  const isApproved = trace.committed_at && !hasRejection;
  const isPending = trace.approval_channel === 'slack' && !trace.committed_at && !hasRejection;
  
  if (isApproved) return 'approved';
  if (hasRejection) return 'rejected';
  if (isPending) return 'pending';
  return 'none';
}

export function ApprovalPill({ trace, state: overrideState }) {
  const state = overrideState || getApprovalState(trace);
  const config = STATES[state] || STATES.none;
  const Icon = config.icon;
  
  return (
    <Badge 
      variant="outline" 
      className={`inline-flex items-center gap-1.5 ${config.className}`}
      data-testid={`approval-pill-${state}`}
    >
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

export default ApprovalPill;
